/**
 * Modal Criar Kit — passo 1: técnicos sem kit | passo 2: produtos do kit
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { addKitProducts, createKits, fetchTechniciansWithoutKit } from '../services/kitTecnicoService';
import KitCreateProductsStep, { isValidKitMaxQty } from './KitCreateProductsStep';
import { technicianInitials } from '../../board/utils/technicianInitials';
import '../../board/components/BoardEncaminharChamadoFlow.css';
import './KitCreateKitModal.css';

/** Avatar compacto no estilo Go: foto S3 (proxy opaco) ou iniciais. */
function KitTechAvatar({ photoUrl, name, initials, size = 'md', className = '' }) {
  const src = String(photoUrl || '').trim();
  const letters =
    String(initials || '').trim() || technicianInitials(name) || '?';
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={`kit-create-tech-avatar kit-create-tech-avatar--img kit-create-tech-avatar--${size} ${className}`.trim()}
        draggable={false}
      />
    );
  }
  return (
    <span
      className={`kit-create-tech-avatar kit-create-tech-avatar--fallback kit-create-tech-avatar--${size} ${className}`.trim()}
      aria-hidden
    >
      {letters}
    </span>
  );
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onCreated?: (created: Array<object>, products?: Array<object>) => void,
 *   mode?: 'create' | 'add',
 *   tech?: { codtech?: string, id?: string, name?: string, nome?: string, profilePhotoUrl?: string, initials?: string } | null,
 *   excludeProductCodes?: string[],
 * }} props
 */
export default function KitCreateKitModal({
  open,
  onClose,
  onCreated,
  mode = 'create',
  tech = null,
  excludeProductCodes = [],
}) {
  const isAdd = mode === 'add';
  const addTech = isAdd
    ? {
        codtech: String(tech?.codtech || tech?.id || '').trim(),
        name: String(tech?.name || tech?.nome || '').trim(),
        profilePhotoUrl: tech?.profilePhotoUrl || null,
        initials: tech?.initials || '',
      }
    : null;
  const [step, setStep] = useState(mode === 'add' ? 'products' : 'techs');
  const [filter, setFilter] = useState('');
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [selectedTechs, setSelectedTechs] = useState(() => new Set());
  const [selectedProducts, setSelectedProducts] = useState(() => new Map());
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  /** idle | creating | success | error */
  const [createPhase, setCreatePhase] = useState('idle');
  const [createMessage, setCreateMessage] = useState('');
  const [createResult, setCreateResult] = useState(null);
  /** @type {Array<{ pedidoGerado?: string, codEmp?: string, codtech?: string, techName?: string }>} */
  const [createPedidos, setCreatePedidos] = useState([]);
  /** @type {Array<{ number?: number|string, message?: string, procedure?: string, codEmp?: string, codtech?: string }>} */
  const [createErrors, setCreateErrors] = useState([]);

  useEffect(() => {
    if (!open) {
      setStep(isAdd ? 'products' : 'techs');
      setFilter('');
      setSelectedTechs(new Set());
      setSelectedProducts(new Map());
      setFeedback(null);
      setLoadError(null);
      setAreas([]);
      setSubmitting(false);
      setCreatePhase('idle');
      setCreateMessage('');
      setCreateResult(null);
      setCreatePedidos([]);
      setCreateErrors([]);
      return undefined;
    }

    if (isAdd) {
      const key = String(addTech?.codtech || '').trim();
      setStep('products');
      setSelectedTechs(key ? new Set([key]) : new Set());
      setLoading(false);
      setLoadError(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await fetchTechniciansWithoutKit();
        if (cancelled) return;
        setAreas(Array.isArray(data?.areas) ? data.areas : []);
      } catch (err) {
        if (cancelled) return;
        setAreas([]);
        setLoadError(err?.message || 'Erro ao carregar técnicos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, isAdd, addTech?.codtech]);

  const finishAfterSuccess = useCallback(() => {
    const created = createResult?.created || [];
    onCreated?.(created, Array.from(selectedProducts.values()));
    setCreatePhase('idle');
    setCreateMessage('');
    setCreateResult(null);
    setCreatePedidos([]);
    setCreateErrors([]);
    setSubmitting(false);
    onClose?.();
  }, [createResult, onCreated, onClose, selectedProducts]);

  const dismissCreateError = useCallback(() => {
    setCreatePhase('idle');
    setCreateMessage('');
    setCreateResult(null);
    setCreatePedidos([]);
    setCreateErrors([]);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Escape só fecha overlays de resultado; o modal principal fecha pelo X (ou Cancelar).
      if (createPhase === 'creating') return;
      if (createPhase === 'success') {
        finishAfterSuccess();
        return;
      }
      if (createPhase === 'error') {
        dismissCreateError();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, createPhase, finishAfterSuccess, dismissCreateError]);

  const requestClose = useCallback(() => {
    if (createPhase === 'creating' || submitting) return;
    if (createPhase === 'success') {
      finishAfterSuccess();
      return;
    }
    if (createPhase === 'error') {
      dismissCreateError();
      return;
    }
    onClose?.();
  }, [createPhase, submitting, finishAfterSuccess, dismissCreateError, onClose]);

  const filteredAreas = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return areas;
    return areas
      .map((area) => {
        const areaName = String(area.name || '').toLowerCase();
        const coord = String(area.coordinatorName || '').toLowerCase();
        const techs =
          areaName.includes(q) || coord.includes(q)
            ? area.technicians || []
            : (area.technicians || []).filter((t) =>
                String(t.name || '')
                  .toLowerCase()
                  .includes(q)
              );
        if (!techs.length) return null;
        return { ...area, technicians: techs };
      })
      .filter(Boolean);
  }, [areas, filter]);

  const selectedTechList = useMemo(() => {
    const map = new Map();
    for (const area of areas) {
      for (const t of area.technicians || []) {
        const key = String(t.codtech || '').trim();
        if (!key || !selectedTechs.has(key) || map.has(key)) continue;
        map.set(key, t);
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' })
    );
  }, [areas, selectedTechs]);

  const nTechSel = isAdd ? (addTech?.codtech ? 1 : 0) : selectedTechs.size;
  const nProdSel = selectedProducts.size;
  const allProductsHaveMaxQty = useMemo(() => {
    if (!nProdSel) return false;
    for (const p of selectedProducts.values()) {
      if (!isValidKitMaxQty(p?.max_qty)) return false;
    }
    return true;
  }, [selectedProducts, nProdSel]);

  const allProductsHaveStockCompany = useMemo(() => {
    if (!nProdSel) return false;
    for (const p of selectedProducts.values()) {
      const sources = Array.isArray(p?.stock_sources) ? p.stock_sources : [];
      if (!sources.some((s) => String(s?.companyCode || '').trim())) return false;
    }
    return true;
  }, [selectedProducts, nProdSel]);

  const canCreateKit = Boolean(nProdSel && allProductsHaveMaxQty && allProductsHaveStockCompany);

  const toggleTech = (codtech) => {
    const key = String(codtech || '').trim();
    if (!key) return;
    setSelectedTechs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleContinueToProducts = () => {
    if (!nTechSel) return;
    setFeedback(null);
    setStep('products');
  };

  const handleConfirmCreate = async () => {
    if (!nTechSel || !canCreateKit || submitting) return;
    if (isAdd && !addTech?.codtech) return;
    setSubmitting(true);
    setFeedback(null);
    setCreatePhase('creating');
    setCreateMessage('');
    setCreateResult(null);
    setCreatePedidos([]);
    setCreateErrors([]);
    try {
      const products = Array.from(selectedProducts.values()).map((p) => ({
        productCode: p.cod_product,
        maxQty: Number(p.max_qty),
        stock_sources: Array.isArray(p.stock_sources)
          ? p.stock_sources.map((s) => ({
              companyCode: String(s.companyCode || '').trim(),
              stockAvailable: Number(s.stockAvailable) || 0,
            }))
          : [],
      }));
      const data = isAdd
        ? await addKitProducts(addTech?.codtech, products)
        : await createKits(Array.from(selectedTechs), products);
      const nTechs = data?.techsCount ?? data?.created?.length ?? nTechSel;
      const nProds = data?.productsCount ?? products.length;
      const pedidosList = Array.isArray(data?.pedidos) ? data.pedidos : [];
      const pedidoNums = pedidosList
        .map((p) => String(p?.pedidoGerado || '').trim())
        .filter(Boolean);
      setCreateResult(data || { created: [] });
      setCreatePedidos(pedidosList);
      setCreateErrors([]);
      const kitPart = isAdd
        ? `${nProds} ${nProds === 1 ? 'produto adicionado' : 'produtos adicionados'} ao kit`
        : nTechs === 1
          ? `Kit criado com ${nProds} ${nProds === 1 ? 'produto' : 'produtos'}`
          : `Kits criados para ${nTechs} técnicos · ${nProds} ${nProds === 1 ? 'produto' : 'produtos'} cada`;
      setCreateMessage(
        pedidoNums.length > 0
          ? `${kitPart}. ${pedidoNums.length} ${pedidoNums.length === 1 ? 'pedido gerado' : 'pedidos gerados'}.`
          : pedidosList.length > 0
            ? `${kitPart}. Pedidos processados.`
            : `${kitPart}.`
      );
      setCreatePhase('success');
    } catch (err) {
      const errList = Array.isArray(err?.errors) ? err.errors : [];
      setCreatePedidos([]);
      setCreateErrors(errList);
      setCreateMessage(err?.message || (isAdd ? 'Erro ao adicionar ao kit.' : 'Erro ao criar kit.'));
      setCreatePhase('error');
    }
  };

  if (!open) return null;

  const selecionadosLabel =
    step === 'techs'
      ? nTechSel === 0
        ? 'Nenhum técnico selecionado'
        : `${nTechSel} ${nTechSel === 1 ? 'técnico selecionado' : 'técnicos selecionados'}`
      : nProdSel === 0
        ? 'Nenhum produto no kit'
        : `${nProdSel} ${nProdSel === 1 ? 'produto no kit' : 'produtos no kit'}`;

  const maxTitleAvatars = 5;
  const displayTechs = addTech ? [addTech] : selectedTechList;
  const titleTechs = displayTechs.slice(0, maxTitleAvatars);
  const titleExtra = Math.max(0, displayTechs.length - maxTitleAvatars);
  const titleName =
    displayTechs.length === 1
      ? displayTechs[0].name
      : `${nTechSel} ${nTechSel === 1 ? 'técnico' : 'técnicos'}`;

  const tree = (
    <div
      className={`board-encaminhar-backdrop kit-create-kit-backdrop${
        step === 'products' ? ' kit-create-kit-backdrop--products' : ''
      }`}
      role="presentation"
    >
      <div
        className={`board-encaminhar-panel kit-create-kit-panel${
          step === 'products' ? ' kit-create-kit-panel--products' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="kit-create-kit-title"
      >
        <div className="kit-create-kit-title-row">
          <h4 id="kit-create-kit-title" className="board-encaminhar-panel-title kit-create-kit-title">
            {isAdd ? 'Adicionar ao Kit' : 'Criar Kit'}
            <span className="board-encaminhar-panel-os kit-create-kit-title-os">
              {step === 'techs' ? (
                'Técnicos sem Kit registrado'
              ) : (
                <span className="kit-create-kit-selected-line">
                  <span className="kit-create-kit-selected-avatars" aria-hidden>
                    {titleTechs.map((t) => (
                      <KitTechAvatar
                        key={String(t.codtech)}
                        photoUrl={t.profilePhotoUrl}
                        name={t.name}
                        initials={t.initials}
                        size="sm"
                        className="kit-create-tech-avatar--stacked"
                      />
                    ))}
                    {titleExtra > 0 ? (
                      <span className="kit-create-tech-avatar kit-create-tech-avatar--more kit-create-tech-avatar--sm">
                        +{titleExtra}
                      </span>
                    ) : null}
                  </span>
                  <span className="kit-create-kit-selected-label">
                    {titleName}
                    <span className="kit-create-kit-selected-suffix"> · selecione os produtos</span>
                  </span>
                </span>
              )}
            </span>
          </h4>
          <button
            type="button"
            className="kit-create-kit-close-btn"
            aria-label="Fechar"
            title="Fechar"
            disabled={createPhase === 'creating' || submitting}
            onClick={requestClose}
          >
            <X size={16} strokeWidth={2.25} aria-hidden />
          </button>
        </div>

        {step === 'techs' ? (
          <>
            {loading ? (
              <p className="board-encaminhar-empty">Carregando técnicos…</p>
            ) : loadError ? (
              <p className="board-encaminhar-feedback is-error">{loadError}</p>
            ) : !areas.length ? (
              <p className="board-encaminhar-empty">Nenhum técnico sem kit.</p>
            ) : (
              <>
                <p className="board-encaminhar-hint">Selecione o(s) técnico(s):</p>
                <input
                  type="text"
                  className="board-encaminhar-filter"
                  placeholder="Filtrar por área ou técnico..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  aria-label="Filtrar técnicos"
                  autoFocus
                />
                <div className="board-encaminhar-tech-list">
                  {filteredAreas.length === 0 ? (
                    <p className="board-encaminhar-empty">Nenhum técnico encontrado.</p>
                  ) : (
                    filteredAreas.map((area) => (
                      <div key={area.id} className="board-encaminhar-area">
                        <span className="board-encaminhar-area-name">
                          {area.name}
                          {area.coordinatorName ? (
                            <span className="kit-create-kit-area-coord"> · {area.coordinatorName}</span>
                          ) : null}
                        </span>
                        <div className="board-encaminhar-techs">
                          {(area.technicians || []).map((tech) => {
                            const key = String(tech.codtech || '').trim();
                            const isSelected = selectedTechs.has(key);
                            return (
                              <button
                                key={`${area.id}-${key}`}
                                type="button"
                                className={`board-encaminhar-tech-btn kit-create-tech-btn${
                                  isSelected ? ' is-selected' : ''
                                }`}
                                onClick={() => toggleTech(key)}
                                aria-pressed={isSelected}
                              >
                                <KitTechAvatar
                                  photoUrl={tech.profilePhotoUrl}
                                  name={tech.name}
                                  initials={tech.initials}
                                  size="md"
                                />
                                <span className="kit-create-tech-btn-name">{tech.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="kit-create-kit-products-wrap">
            <KitCreateProductsStep
              selectedMap={selectedProducts}
              onChangeSelected={setSelectedProducts}
              excludeProductCodes={excludeProductCodes}
            />
          </div>
        )}

        {feedback ? <p className="board-encaminhar-feedback is-error">{feedback}</p> : null}

        <div className="board-encaminhar-actions kit-create-kit-actions">
          <div className="kit-create-kit-sel" aria-live="polite">
            {selecionadosLabel}
          </div>
          {step === 'techs' ? (
            <>
              <button
                type="button"
                className="user-form-link-btn user-form-footer-btn"
                disabled={submitting}
                onClick={() => onClose?.()}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="user-form-profile-save-btn user-form-footer-btn"
                disabled={!nTechSel || loading || Boolean(loadError)}
                onClick={handleContinueToProducts}
              >
                Continuar
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="user-form-link-btn user-form-footer-btn"
                disabled={submitting}
                onClick={() => {
                  if (isAdd) {
                    requestClose();
                    return;
                  }
                  setFeedback(null);
                  setStep('techs');
                }}
              >
                {isAdd ? 'Cancelar' : 'Voltar'}
              </button>
              <button
                type="button"
                className="user-form-profile-save-btn user-form-footer-btn"
                disabled={submitting || !canCreateKit}
                title={
                  !nProdSel
                    ? 'Selecione ao menos um produto'
                    : !allProductsHaveMaxQty
                      ? 'Informe a quantidade máxima (mín. 1) de cada produto'
                      : !allProductsHaveStockCompany
                        ? 'Selecione ao menos uma empresa de estoque em cada produto'
                        : undefined
                }
                onClick={handleConfirmCreate}
              >
                {submitting
                  ? isAdd
                    ? 'Adicionando…'
                    : 'Criando…'
                  : isAdd
                    ? 'Adicionar ao Kit'
                    : 'Criar Kit padrão'}
              </button>
            </>
          )}
        </div>

        {createPhase !== 'idle' ? (
          <div
            className={`kit-create-result-overlay kit-create-result-overlay--${createPhase}`}
            role="status"
            aria-live="polite"
            aria-busy={createPhase === 'creating'}
          >
            <div className="kit-create-result-card">
              {createPhase === 'creating' ? (
                <>
                  <div className="kit-create-result-spinner" aria-hidden />
                  <p className="kit-create-result-title">{isAdd ? 'Adicionando ao kit…' : 'Criando kit…'}</p>
                  <p className="kit-create-result-text">
                    {isAdd
                      ? 'Gravando produtos e gerando pedidos.'
                      : `Gravando produtos para ${nTechSel} ${nTechSel === 1 ? 'técnico' : 'técnicos'}.`}
                  </p>
                </>
              ) : null}
              {createPhase === 'success' ? (
                <>
                  <div className="kit-create-result-icon kit-create-result-icon--ok" aria-hidden>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 12.5l4.2 4.2L19 7"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="kit-create-result-title">{isAdd ? 'Produtos adicionados' : 'Kit criado'}</p>
                  <p className="kit-create-result-text">{createMessage}</p>
                  {createPedidos.some((p) => String(p?.pedidoGerado || '').trim()) ? (
                    <ul className="kit-create-result-list kit-create-result-list--ok" aria-label="Pedidos gerados">
                      {createPedidos.map((p, idx) => {
                        const num = String(p?.pedidoGerado || '').trim();
                        if (!num) return null;
                        const emp = String(p?.codEmp || '').trim();
                        const tech = String(p?.techName || p?.codtech || '').trim();
                        return (
                          <li key={`${num}-${emp}-${idx}`}>
                            <span className="kit-create-result-list-code">{num}</span>
                            {(emp || tech) && (
                              <span className="kit-create-result-list-meta">
                                {[emp ? `Emp. ${emp}` : null, tech || null].filter(Boolean).join(' · ')}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  <button
                    type="button"
                    className="user-form-profile-save-btn user-form-footer-btn kit-create-result-btn"
                    onClick={finishAfterSuccess}
                    autoFocus
                  >
                    Concluir
                  </button>
                </>
              ) : null}
              {createPhase === 'error' ? (
                <>
                  <div className="kit-create-result-icon kit-create-result-icon--err" aria-hidden>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 8v5.5M12 16.5h.01M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <p className="kit-create-result-title">
                    {isAdd ? 'Não foi possível adicionar' : 'Não foi possível criar'}
                  </p>
                  <p className="kit-create-result-text">{createMessage}</p>
                  {createErrors.length > 0 ? (
                    <ul className="kit-create-result-list kit-create-result-list--err" aria-label="Erros">
                      {createErrors.map((e, idx) => {
                        const code = e?.number != null ? String(e.number) : '';
                        const msg = String(e?.message || '').trim();
                        const emp = String(e?.codEmp || '').trim();
                        const tech = String(e?.codtech || '').trim();
                        const proc = String(e?.procedure || '').trim();
                        return (
                          <li key={`${code}-${idx}`}>
                            {code ? (
                              <span className="kit-create-result-list-code">#{code}</span>
                            ) : null}
                            <span className="kit-create-result-list-msg">
                              {msg || 'Erro na procedure'}
                            </span>
                            {(emp || tech || proc) && (
                              <span className="kit-create-result-list-meta">
                                {[
                                  emp ? `Emp. ${emp}` : null,
                                  tech ? `Téc. ${tech}` : null,
                                  proc || null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  <button
                    type="button"
                    className="user-form-profile-save-btn user-form-footer-btn kit-create-result-btn"
                    onClick={dismissCreateError}
                    autoFocus
                  >
                    Entendi
                  </button>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(tree, document.body);
}
